import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Alert,
  Container,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
} from "@mui/material";
import {
  CloudUploadOutlined,
  CheckCircle,
  Cancel,
  ArrowBack,
  Visibility,
  Edit,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { useAuth } from "../hooks/useAuth";
import { uiPT } from "../i18n";
import Card from "../components/Card";
import { cleanWhatsAppNumber, validateWhatsAppNumber, displayWhatsAppNumber } from "../utils/phoneUtils";
import { MuiTelInput } from 'mui-tel-input';

interface Partner {
  _id: string;
  partnerCode: string;
  name: string;
  IBAN: string;
  referredFee: number;
  referralLink: string;
  isActive: boolean;
}

interface Client {
  _id: string;
  trackingCode: string;
  name: string;
  email: string;
  type: string;
  modality: string;
  phoneNumber: string;
  IBAN: string;
  identityType: string;
  city: string;
  status: string;
  isReferred: boolean;
  partnerId?: string;
  idCard: {
    front?: string;
    back?: string;
    pdf?: string;
  };
  selfie: string;
  modalityPhoto: string;
  driverLicense?: string;
  caeDocument?: string;
  taxClearanceDocument?: string;
  createdAt: string;
  updatedAt: string;
}

interface FormData {
  name: string;
  email: string;
  type: string;
  modality: string;
  phoneNumber: string;
  IBAN: string;
  identityType: string;
  city: string;
  idCardType: "pdf" | "image";
  selfie?: File;
  modalityPhoto?: File;
  driverLicense?: File;
  idCardFront?: File;
  idCardBack?: File;
  idCardPdf?: File;
  caeDocument?: File;
  taxClearanceDocument?: File;
}

const schema = yup.object({
  name: yup
    .string()
    .required("Nome é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres"),
  email: yup
    .string()
    .required("Email é obrigatório")
    .email("Email deve ter um formato válido"),
  type: yup
    .string()
    .required("Tipo é obrigatório")
    .oneOf(["courier", "driver"], "Tipo inválido"),
  modality: yup
    .string()
    .required("Modalidade é obrigatória")
    .oneOf(["bike", "motorcycle"], "Modalidade inválida"),
  phoneNumber: yup
    .string()
    .when('$isReferred', {
      is: false,
      then: (schema) => schema
        .required("Telefone WhatsApp é obrigatório")
        .test('whatsapp-validation', 'Número WhatsApp deve ser um número internacional válido (ex: +351 912 345 678, +1 555 123 4567)', (value) => validateWhatsAppNumber(value || '')),
      otherwise: (schema) => schema.optional(),
    }),
  IBAN: yup
    .string()
    .required("IBAN é obrigatório")
    .transform((value) => (value ? value.replace(/\s/g, "") : value))
    .matches(
      /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/,
      "IBAN inválido"
    ),
  identityType: yup
    .string()
    .required("Tipo de identidade é obrigatório")
    .oneOf(["passport", "cc", "trc", "cplp"], "Tipo de identidade inválido"),
  city: yup
    .string()
    .required("Cidade é obrigatória")
    .min(2, "Cidade deve ter pelo menos 2 caracteres"),
  idCardType: yup
    .string()
    .required("Tipo de documento é obrigatório")
    .oneOf(["pdf", "image"], "Tipo inválido"),
  selfie: yup.mixed().optional(),
  modalityPhoto: yup.mixed().optional(),
  driverLicense: yup.mixed().when("modality", {
    is: "motorcycle",
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.optional(),
  }),
  idCardFront: yup.mixed().optional(),
  idCardBack: yup.mixed().optional(),
  idCardPdf: yup.mixed().optional(),
  caeDocument: yup.mixed().optional(),
  taxClearanceDocument: yup.mixed().optional(),
}) as yup.ObjectSchema<FormData>;

export default function ClientEditPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>(
    {}
  );
  const [existingFiles, setExistingFiles] = useState<Record<string, string>>(
    {}
  );
  const [partner, setPartner] = useState<Partner | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    context: { isReferred: client?.isReferred || false },
    defaultValues: {
      name: "",
      email: "",
      type: "",
      modality: "",
      phoneNumber: "",
      IBAN: "",
      identityType: "",
      city: "",
      idCardType: "image",
    },
  });

  const modality = watch("modality");
  const idCardType = watch("idCardType");

  useEffect(() => {
    if (id) {
      fetchClient();
    }
  }, [id]);

  const fetchClient = async () => {
    if (!id) return;
    
    setFetchLoading(true);
    try {
      const response = await api.get(`/clients/${id}`);
      const clientData = response.data.data;
      setClient(clientData);

      // Fetch partner information if client is referred
      if (clientData.isReferred && clientData.partnerId) {
        try {
          const partnerResponse = await api.get(`/partners`);
          const partners = partnerResponse.data.data;
          const clientPartner = partners.find((p: Partner) => p._id === clientData.partnerId);
          if (clientPartner) {
            setPartner(clientPartner);
          }
        } catch (partnerError) {
          console.error('Error fetching partner data:', partnerError);
        }
      }

      // Determine ID card type based on existing files
      const hasIdCardPdf = clientData.idCard?.pdf;
      const idCardType = hasIdCardPdf ? "pdf" : "image";

      // Pre-populate form with existing data
      reset({
        name: clientData.name,
        email: clientData.email,
        type: clientData.type,
        modality: clientData.modality,
        phoneNumber: clientData.phoneNumber ? displayWhatsAppNumber(clientData.phoneNumber) : '',
        IBAN: formatIBAN(clientData.IBAN),
        identityType: clientData.identityType,
        city: clientData.city,
        idCardType,
      });

      // Set existing files for display
      const existing: Record<string, string> = {};
      if (clientData.selfie) existing.selfie = clientData.selfie;
      if (clientData.modalityPhoto) existing.modalityPhoto = clientData.modalityPhoto;
      if (clientData.driverLicense) existing.driverLicense = clientData.driverLicense;
      if (clientData.idCard?.front) existing.idCardFront = clientData.idCard.front;
      if (clientData.idCard?.back) existing.idCardBack = clientData.idCard.back;
      if (clientData.idCard?.pdf) existing.idCardPdf = clientData.idCard.pdf;
      if (clientData.caeDocument) existing.caeDocument = clientData.caeDocument;
      if (clientData.taxClearanceDocument) existing.taxClearanceDocument = clientData.taxClearanceDocument;
      
      setExistingFiles(existing);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar dados do cliente");
    } finally {
      setFetchLoading(false);
    }
  };

  // IBAN mask function
  const formatIBAN = (value: string) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const formatted = cleaned.replace(/(.{4})/g, "$1 ").trim();
    return formatted;
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Add form fields
      formData.append("name", data.name);
      formData.append("email", data.email);
      formData.append("type", data.type);
      formData.append("modality", data.modality);
      
      // Add phoneNumber only if client is not referred
      if (!client?.isReferred) {
        formData.append("phoneNumber", cleanWhatsAppNumber(data.phoneNumber));
      }
      
      formData.append("IBAN", data.IBAN.replace(/\s/g, ""));
      formData.append("identityType", data.identityType);
      formData.append("city", data.city);

      // Add file fields only if they are new uploads
      if (data.selfie) formData.append("selfie", data.selfie);
      if (data.modalityPhoto) formData.append("modalityPhoto", data.modalityPhoto);
      if (data.driverLicense) formData.append("driverLicense", data.driverLicense);
      if (data.idCardFront) formData.append("idCardFront", data.idCardFront);
      if (data.idCardBack) formData.append("idCardBack", data.idCardBack);
      if (data.idCardPdf) formData.append("idCardPdf", data.idCardPdf);
      if (data.caeDocument) formData.append("caeDocument", data.caeDocument);
      if (data.taxClearanceDocument) formData.append("taxClearanceDocument", data.taxClearanceDocument);

      await api.put(`/clients/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess("Cliente atualizado com sucesso!");
      // Refresh client data
      fetchClient();
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao atualizar cliente");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setValue(field, file);
        setUploadedFiles((prev) => ({
          ...prev,
          [field]: file.name,
        }));
      }
    };

  const removeFile = (field: keyof FormData) => {
    setValue(field, undefined);
    setUploadedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[field];
      return newFiles;
    });
  };

  const openFilePreview = (filename: string) => {
    const proxyUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/files/${filename}`;
    window.open(proxyUrl, '_blank');
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Cliente não encontrado</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) => theme.palette.grey[100],
        py: 3,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800 }}
            >
              Editar Cliente
            </Typography>
          </Box>

          {/* Client Info */}
          <Card sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Edit sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {client.name}
                </Typography>
                <Typography component="span" variant="body2" color="text.secondary">
                  Código: {client.trackingCode} | Status: <Chip 
                    label={client.status}
                    size="small"
                    color={
                      client.status === 'ATIVA' ? 'success' :
                      client.status === 'REJEITADO PELA EQUIPE' ? 'error' :
                      'warning'
                    }
                  />
                </Typography>
              </Box>
            </Stack>
          </Card>

          <Card sx={{ p: 4 }}>
            <Stack spacing={4}>
              {/* Icon Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (theme) => theme.shadows[3],
                  }}
                >
                  <Edit sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Box>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
              {/* Basic Information */}
              <Typography
                variant="h6"
                sx={{ color: "#2c3e50", fontWeight: 600 }}
              >
                Informações Básicas
              </Typography>

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={uiPT.register.name}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email"
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label={uiPT.register.type}
                      error={!!errors.type}
                      helperText={errors.type?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    >
                      <MenuItem value="courier">
                        {uiPT.register.courier}
                      </MenuItem>
                      <MenuItem value="driver">{uiPT.register.driver}</MenuItem>
                    </TextField>
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Controller
                  name="modality"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label={uiPT.register.modality}
                      error={!!errors.modality}
                      helperText={errors.modality?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    >
                      <MenuItem value="bike">{uiPT.register.bike}</MenuItem>
                      <MenuItem value="motorcycle">
                        {uiPT.register.motorcycle}
                      </MenuItem>
                    </TextField>
                  )}
                />
                {!client?.isReferred && (
                      <Controller
                        name="phoneNumber"
                        control={control}
                        render={({ field }) => (
                          <MuiTelInput
                            {...field}
                            label="Telefone WhatsApp"
                            placeholder="+351 912 345 678"
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber?.message}
                            required
                            defaultCountry="PT"
                            preferredCountries={['PT', 'BR', 'US', 'GB', 'FR', 'ES', 'DE', 'IT']}
                            sx={{
                              flex: 1,
                              minWidth: 250,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                "&:hover fieldset": {
                                  borderColor: "#667eea",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#667eea",
                                },
                              },
                            }}
                          />
                        )}
                      />
                )}
              </Box>

              {/* Referred Client Information */}
              {client?.isReferred && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "#2c3e50", fontWeight: 600 }}
                  >
                    Cliente Indicado
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Chip 
                      label="Cliente Indicado por Parceiro" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                    {partner && (
                      <Chip 
                        label={`Parceiro: ${partner.name}`} 
                        color="secondary" 
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Controller
                  name="IBAN"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={uiPT.register.IBAN}
                      placeholder="PT50 1234 5678 9012 3456 7890 1"
                      error={!!errors.IBAN}
                      helperText={errors.IBAN?.message}
                      required
                      onChange={(e) => {
                        const formatted = formatIBAN(e.target.value);
                        field.onChange(formatted);
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="identityType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Tipo de Identidade"
                      error={!!errors.identityType}
                      helperText={errors.identityType?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    >
                      <MenuItem value="passport">Passaporte</MenuItem>
                      <MenuItem value="cc">Cartão de cidadão (CC)</MenuItem>
                      <MenuItem value="trc">Autorização de residência (TRC)</MenuItem>
                      <MenuItem value="cplp">CPLP</MenuItem>
                    </TextField>
                  )}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={uiPT.register.city}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* ID Card Type Selection */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#2c3e50", fontWeight: 600 }}
                >
                  Tipo de Documento de Identidade
                </Typography>
                <Controller
                  name="idCardType"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      component="fieldset"
                      error={!!errors.idCardType}
                    >
                      <FormLabel
                        component="legend"
                        sx={{ color: "#2c3e50", fontWeight: 600, mb: 1 }}
                      >
                        Escolha o formato do documento:
                      </FormLabel>
                      <RadioGroup
                        {...field}
                        row
                        sx={{
                          "& .MuiFormControlLabel-root": {
                            marginRight: 3,
                          },
                          "& .MuiRadio-root": {
                            color: "#667eea",
                            "&.Mui-checked": {
                              color: "#667eea",
                            },
                          },
                        }}
                      >
                        <FormControlLabel
                          value="image"
                          control={<Radio />}
                          label="Imagens (Frente e Verso)"
                        />
                        <FormControlLabel
                          value="pdf"
                          control={<Radio />}
                          label="PDF"
                        />
                      </RadioGroup>
                      {errors.idCardType && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, display: "block" }}
                        >
                          {errors.idCardType.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>

              {/* Document Upload Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#2c3e50", fontWeight: 600 }}
                >
                  Documentos (opcional - apenas para substituir)
                </Typography>
              </Box>

              {/* ID Card Documents */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                  Documento de Identidade
                </Typography>
                <Stack spacing={2}>
                  {idCardType === "image" ? (
                    <>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.idCardFront}
                            </Typography>
                            <Chip label="Opcional" color="warning" size="small" />
                          </Box>
                          
                          {/* Show existing file */}
                          {existingFiles.idCardFront && (
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Atual:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                  {existingFiles.idCardFront}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => openFilePreview(existingFiles.idCardFront)}
                                >
                                  <Visibility sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          )}
                          
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange("idCardFront")}
                            />
                          </Button>
                          {uploadedFiles.idCardFront && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                Novo: {uploadedFiles.idCardFront}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("idCardFront")}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.idCardFront && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.idCardFront.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.idCardBack}
                            </Typography>
                            <Chip label="Opcional" color="warning" size="small" />
                          </Box>
                          
                          {/* Show existing file */}
                          {existingFiles.idCardBack && (
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Atual:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                  {existingFiles.idCardBack}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => openFilePreview(existingFiles.idCardBack)}
                                >
                                  <Visibility sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          )}
                          
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange("idCardBack")}
                            />
                          </Button>
                          {uploadedFiles.idCardBack && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                Novo: {uploadedFiles.idCardBack}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("idCardBack")}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.idCardBack && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.idCardBack.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>
                    </>
                  ) : (
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {uiPT.register.idCardPdf}
                          </Typography>
                          <Chip label="Opcional" color="warning" size="small" />
                        </Box>
                        
                        {/* Show existing file */}
                        {existingFiles.idCardPdf && (
                          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Atual:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                {existingFiles.idCardPdf}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => openFilePreview(existingFiles.idCardPdf)}
                              >
                                <Visibility sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        )}
                        
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<CloudUploadOutlined />}
                          fullWidth
                          sx={{ justifyContent: 'center' }}
                        >
                          {uiPT.resubmit.selectFile}
                          <input
                            type="file"
                            accept="application/pdf"
                            hidden
                            onChange={handleFileChange("idCardPdf")}
                          />
                        </Button>
                        {uploadedFiles.idCardPdf && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                              Novo: {uploadedFiles.idCardPdf}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeFile("idCardPdf")}
                              sx={{ color: 'error.main' }}
                            >
                              <Cancel sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        )}
                        {errors.idCardPdf && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            {errors.idCardPdf.message}
                          </Typography>
                        )}
                      </Stack>
                    </Card>
                  )}
                </Stack>
              </Box>

              {/* Other Documents */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                  Outros Documentos
                </Typography>
                <Stack spacing={2}>
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {uiPT.register.selfie}
                        </Typography>
                        <Chip label="Opcional" color="warning" size="small" />
                      </Box>
                      
                      {/* Show existing file */}
                      {existingFiles.selfie && (
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Atual:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                              {existingFiles.selfie}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openFilePreview(existingFiles.selfie)}
                            >
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                      
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleFileChange("selfie")}
                        />
                      </Button>
                      {uploadedFiles.selfie && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            Novo: {uploadedFiles.selfie}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("selfie")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.selfie && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.selfie.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>

                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {uiPT.register.modalityPhoto}
                        </Typography>
                        <Chip label="Opcional" color="warning" size="small" />
                      </Box>
                      
                      {/* Show existing file */}
                      {existingFiles.modalityPhoto && (
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Atual:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                              {existingFiles.modalityPhoto}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openFilePreview(existingFiles.modalityPhoto)}
                            >
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                      
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleFileChange("modalityPhoto")}
                        />
                      </Button>
                      {uploadedFiles.modalityPhoto && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            Novo: {uploadedFiles.modalityPhoto}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("modalityPhoto")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.modalityPhoto && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.modalityPhoto.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>

                  {modality === 'motorcycle' && (
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {uiPT.register.driverLicense}
                          </Typography>
                          <Chip label="Opcional" color="warning" size="small" />
                        </Box>
                        
                        {/* Show existing file */}
                        {existingFiles.driverLicense && (
                          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Atual:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                {existingFiles.driverLicense}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => openFilePreview(existingFiles.driverLicense)}
                              >
                                <Visibility sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        )}
                        
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<CloudUploadOutlined />}
                          fullWidth
                          sx={{ justifyContent: 'center' }}
                        >
                          {uiPT.resubmit.selectFile}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleFileChange("driverLicense")}
                          />
                        </Button>
                        {uploadedFiles.driverLicense && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                              Novo: {uploadedFiles.driverLicense}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeFile("driverLicense")}
                              sx={{ color: 'error.main' }}
                            >
                              <Cancel sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        )}
                        {errors.driverLicense && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            {errors.driverLicense.message}
                          </Typography>
                        )}
                      </Stack>
                    </Card>
                  )}

                  {/* CAE Document */}
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Código de Atividade Económica (CAE)
                        </Typography>
                        <Chip label="Opcional" color="warning" size="small" />
                      </Box>
                      
                      {/* Show existing file */}
                      {existingFiles.caeDocument && (
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Atual:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                              {existingFiles.caeDocument}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openFilePreview(existingFiles.caeDocument)}
                            >
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                      
                      <Typography variant="body2" color="text.secondary">
                        Documento PDF do Código de Atividade Económica
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="application/pdf"
                          hidden
                          onChange={handleFileChange("caeDocument")}
                        />
                      </Button>
                      {uploadedFiles.caeDocument && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            Novo: {uploadedFiles.caeDocument}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("caeDocument")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.caeDocument && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.caeDocument.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>

                  {/* Tax Clearance Document */}
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Certidão de Não Dívida - Autoridade Tributária
                        </Typography>
                        <Chip label="Opcional" color="warning" size="small" />
                      </Box>
                      
                      {/* Show existing file */}
                      {existingFiles.taxClearanceDocument && (
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Atual:
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                              {existingFiles.taxClearanceDocument}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openFilePreview(existingFiles.taxClearanceDocument)}
                            >
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                      
                      <Typography variant="body2" color="text.secondary">
                        Documento PDF da Certidão de Não Dívida da Autoridade Tributária
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="application/pdf"
                          hidden
                          onChange={handleFileChange("taxClearanceDocument")}
                        />
                      </Button>
                      {uploadedFiles.taxClearanceDocument && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            Novo: {uploadedFiles.taxClearanceDocument}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("taxClearanceDocument")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.taxClearanceDocument && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.taxClearanceDocument.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>
                </Stack>
              </Box>
            </Stack>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "#d32f2f",
                  },
                }}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "#2e7d32",
                  },
                }}
              >
                {success}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              {loading ? "Atualizando..." : "Atualizar Cliente"}
            </Button>
          </form>
        </Stack>
      </Card>
    </Stack>
  </Container>
</Box>
);
} 